import React, { useCallback, useEffect, useState } from 'react';

type SpeedSource = 'PROCESS' | 'EQUIPMENT';

const OPTIONS: Array<{ value: SpeedSource; title: string; hint: string }> = [
  {
    value: 'PROCESS',
    title: 'Calcula tiempos en base a proceso',
    hint: 'Obtiene la velocidad del catálogo de Tipos de Procesos (según el tipo de proceso de la orden).',
  },
  {
    value: 'EQUIPMENT',
    title: 'Calcula tiempos en base a velocidad de equipo',
    hint: 'Obtiene la velocidad del catálogo de Equipos (según la máquina asignada).',
  },
];

async function readError(res: Response, fallback: string): Promise<string> {
  const body = await res.json().catch(() => ({}));
  return body?.error || fallback;
}

const PlanningGeneralSettings: React.FC = () => {
  const [saved, setSaved] = useState<SpeedSource | null>(null);
  const [draft, setDraft] = useState<SpeedSource | null>(null);
  const [savedEff, setSavedEff] = useState('');
  const [effDraft, setEffDraft] = useState(''); // Eficiencia General %, '' = not set (assume 100%)
  const [savedReuse, setSavedReuse] = useState('');
  const [reuseDraft, setReuseDraft] = useState(''); // % of setup saved when the next order reuses the machine's colors, '' = off
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/planning/general-settings', { credentials: 'include' });
      if (!res.ok) throw new Error(await readError(res, 'Error al cargar la configuración general'));
      const data = await res.json();
      const eff = data.generalEfficiencyPct == null ? '' : String(data.generalEfficiencyPct);
      setSaved(data.speedSource);
      setDraft(data.speedSource);
      const reuse = data.setupReusePct == null ? '' : String(data.setupReusePct);
      setSavedEff(eff);
      setEffDraft(eff);
      setSavedReuse(reuse);
      setReuseDraft(reuse);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!draft) return;
    const effNum = effDraft.trim() === '' ? null : Number(effDraft);
    if (effNum !== null && (!Number.isFinite(effNum) || effNum <= 0 || effNum > 100)) {
      setError('La eficiencia general debe ser mayor a 0 y máximo 100');
      return;
    }
    const reuseNum = reuseDraft.trim() === '' ? null : Number(reuseDraft);
    if (reuseNum !== null && (!Number.isFinite(reuseNum) || reuseNum < 0 || reuseNum > 100)) {
      setError('El ahorro de setup debe estar entre 0 y 100');
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch('/api/planning/general-settings', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ speedSource: draft, generalEfficiencyPct: effNum, setupReusePct: reuseNum }),
      });
      if (!res.ok) throw new Error(await readError(res, 'Error al guardar la configuración general'));
      const data = await res.json();
      const eff = data.generalEfficiencyPct == null ? '' : String(data.generalEfficiencyPct);
      setSaved(data.speedSource);
      setDraft(data.speedSource);
      const reuse = data.setupReusePct == null ? '' : String(data.setupReusePct);
      setSavedEff(eff);
      setEffDraft(eff);
      setSavedReuse(reuse);
      setReuseDraft(reuse);
      setNotice('Configuración guardada. Aplica a las próximas simulaciones y movimientos.');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
      <div>
        <h2 className="text-lg font-bold text-gray-800">⏱️ Cálculo de tiempos de producción</h2>
        <p className="text-sm text-gray-500 mt-1">
          Elige de dónde se toma la velocidad de corrido para calcular el tiempo de cada orden en las simulaciones y el tablero de planeación.
        </p>
      </div>

      {loading && <p className="text-sm text-gray-500">Cargando...</p>}
      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
      {notice && <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{notice}</p>}

      {draft && (
        <>
          <div role="radiogroup" aria-label="Origen de la velocidad" className="space-y-2">
            {OPTIONS.map((o) => (
              <label
                key={o.value}
                className={`flex items-start gap-3 border rounded-lg px-3 py-2 cursor-pointer ${draft === o.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
              >
                <input
                  type="radio"
                  name="speedSource"
                  className="mt-1"
                  checked={draft === o.value}
                  disabled={saving}
                  onChange={() => { setNotice(null); setDraft(o.value); }}
                />
                <span>
                  <span className="block text-sm font-medium text-gray-800">{o.title}</span>
                  <span className="block text-xs text-gray-500">{o.hint}</span>
                </span>
              </label>
            ))}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-1" htmlFor="generalEfficiency">Eficiencia general (%)</label>
            <input
              id="generalEfficiency"
              type="number"
              min="1"
              max="100"
              step="1"
              value={effDraft}
              disabled={saving}
              onChange={(e) => { setNotice(null); setEffDraft(e.target.value); }}
              className="w-32 p-2 border border-gray-300 rounded-md"
              placeholder="Ej: 50"
            />
            <p className="text-xs text-gray-500 mt-1">
              Se usa cuando el equipo no tiene eficiencia propia en su catálogo (tiempo real = tiempo teórico / eficiencia).
              Vacío: se asume 100%, que subestima el tiempo real.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-1" htmlFor="setupReuse">Ahorro de setup por colores en común (%)</label>
            <input
              id="setupReuse"
              type="number"
              min="0"
              max="100"
              step="1"
              value={reuseDraft}
              disabled={saving}
              onChange={(e) => { setNotice(null); setReuseDraft(e.target.value); }}
              className="w-32 p-2 border border-gray-300 rounded-md"
              placeholder="Ej: 40"
            />
            <p className="text-xs text-gray-500 mt-1">
              Máximo porcentaje del setup que se ahorra cuando la orden siguiente en la misma máquina usa los mismos colores que la anterior
              (proporcional a los colores compartidos). Vacío o 0: el setup completo en cada orden.
            </p>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={save}
              disabled={saving || (draft === saved && effDraft === savedEff && reuseDraft === savedReuse)}
              className="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white disabled:opacity-50"
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PlanningGeneralSettings;
